<template>
  <div class="modern-color-theme font-poppins flex flex-col gap-2" data-component-name="VSmartInput">
    <VSLabel v-if="props.label" :for="id" :tooltip="props.labelTooltip" @click="contentEditableElement?.focus()">
      {{ props.label }}
    </VSLabel>
    <div
      class="rounded-md ring-1 ring-inset relative overflow-y-auto overflow-x-hidden py-[calc(0.375rem-1px)] px-[calc(0.625rem+2px)]"
      :class="{
        'text-neutral-400 bg-neutral-150 ring-neutral-200 cursor-not-allowed select-none': props.disabled,
        'text-neutral-950 bg-neutral-150 ring-0 focus-within:ring-2 focus-within:ring-inset focus-within:ring-blue-600': !props.disabled && props.readonly,
        'text-neutral-950 bg-neutral-100 ring-neutral-300 focus-within:ring-2 focus-within:ring-inset focus-within:ring-blue-600': !props.disabled && !props.readonly,
        'resize-y': props.multiline 
      }"
    >
      <p
        :id="id"
        ref="content-editable"
        :aria-labelledby="`${id}-label`"
        :role="props.disabled ? 'none' : 'textbox'"
        :contenteditable="contentEditable"
        class="rounded-md block text-sm leading-6 font-normal py-[1px] !border-none !outline-none !shadow-none whitespace-pre-wrap"
        :style="computedStyle"
        :tabindex="props.disabled ? -1 : 0"
        :name="props.name"
        :aria-describedby="descriptionId"
        @input="updateModelValueInternal"
        @click="menuOpen = false"
        @keydown.enter="handleEnter"
        @keydown="(event) => { checkAutoComplete(event); scheduleFlush() }"
        @paste="handlePaste"
        @focus="onFocus"
        @blur="onBlur"
      />
      <div v-if="!modelValueInternal" class="text-neutral-500 absolute inset-0 pointer-events-none px-3 text-sm pt-2">
        {{ props.placeholder }}
      </div>
      <div v-if="props.maxLength" class="absolute bottom-0 right-0 p-2 pr-3 whitespace-nowrap pointer-events-none text-xs leading-4 font-normal" :class="{ 'text-neutral-500': !props.disabled, 'text-neutral-250': props.disabled }" role="status" aria-live="polite">
        {{ modelValueInternalLength }} / {{ props.maxLength }}
      </div>
      <VSResetButton v-if="modelValueInternal" container="absolute top-0 right-2 bottom-0 items-center flex" v-bind="props" @click="reset" />
      <VTeleport v-if="menuOpen" to="body">
        <div ref="menu" class="modern-color-theme font-poppins fixed z-[1001] mt-1 rounded-md bg-neutral-100 shadow-sm ring-1 ring-inset min-w-[300px] w-[300px] ring-neutral-250 flex flex-col gap-1 p-1 max-h-96" :style="position">
          <VInput v-model="searchModel" :placeholder="props.searchPlaceholder" focus @keydown.esc="menuOpen = false" @keydown.enter.prevent="add">
            <template #suffix>
              <button v-if="searchModel && props.open" :disabled="!canAdd" type="button" class="flex items-center justify-center" @click="add">
                <VIcon color="neutral" name="Solid/add-box" />
              </button>
              <VIcon v-else color="neutral" name="Solid/search" />
            </template>
          </VInput>
          <ul class="flex flex-col gap-1 overflow-y-auto h-64" tabindex="-1">
            <li v-for="(variable) of searchCollection" :key="variable.value" class="w-full">
              <button type="button" class="px-4 pl-3 py-2 w-full text-sm leading-5 rounded-md flex gap-2 items-center whitespace-nowrap cursor-pointer justify-between hover:bg-primary-150 text-neutral-850" :class="{ 'bg-primary-150': selectedVariable?.value === variable.value }" @click="select(variable)">
                {{ variable.label ?? variable.value }}
                <VIcon v-if="variable.icon" dense :name="variable.icon" />
              </button>
            </li>
          </ul>
          <div v-if="props.requireDefault" class="grid grid-cols-2 gap-x-1 pr-1">
            <VInput ref="default" v-model="selectedDefault" :placeholder="localize('default_placeholder')">
              <template #prefix>
                <VIcon color="neutral" name="Outline/edit" />
              </template>
            </VInput>
            <VButton wide :disabled="!selectedVariable || (props.requireDefault === true && !selectedDefault)" class="border-none" @click="insert">
              {{ localize('insert') }}
            </VButton>
          </div>
        </div>
      </VTeleport>
    </div>
    <VSDescription v-safe-html="props.triggerDescription" />
    <VSValidation v-if="validationResult" :type="validationResult[0]" :message="validationResult[1]" />
    <VSDescription v-if="props.description" :id="descriptionId" v-safe-html="props.description" />
  </div>
</template>
<script setup lang="ts">
import { computed, ref, useTemplateRef, watchEffect } from 'vue';
import VSDescription from './components/VSDescription.vue'
import VSLabel from './components/VSLabel.vue'
import { useElementId, useDescriptionElementId } from '../../utils/utils';
import VIcon from '../labels/VIcon.vue';
import VInput from './VInput.vue';
import VButton from '../buttons/VButton.vue';
import { convertAutoNameToSmartString, convertHtmlToSmartString, convertSmartStringToAutoName, convertSmartStringToHtml, createSpan, extractVariables, measureSmartStringLength } from '../../utils/smart-strings';
import { useSearch } from '@utils/search';
import { onClickOutsideOf } from '@utils/focus';
import type { V } from '@utils/types';
import { findSelectOption } from './helpers/select';
import { globalLocalize, useLocalize } from '@utils/localization';
import VTeleport from '@library/utilities/VTeleport.vue';
import { stopAndPrevent } from '@utils/events'
import { useBuffering } from './helpers/buffering';
import { SHARED_PROPS_DEFAULTS, VALIDATION_PROPS_DEFAULTS, type BufferingProps, type PlaceholderProps, type SharedProps, type ValidationProps } from './helpers/types';
import { createDefaultSmartInputValidation, useValidation } from './helpers/validations';
import VSValidation from './components/VSValidation.vue';
import { useAnimationFramePosition } from '@utils/position';
import VSResetButton from './components/VSResetButton.vue';

const localize = useLocalize('component-library.inputs.smart_input')

defineOptions({
  name: 'VSmartInput'
})

const props = withDefaults(
  defineProps<SharedProps & ValidationProps<string> & PlaceholderProps & BufferingProps & {
    variables: V.SmartInput.Variable[]
    /**
     * Use autoname syntax instead of smartstrings
     */
    autoname?: boolean
    /**
     * Maximum length of content (used for validation as well as indication to the user)
     */
    maxLength?: number
    open?: boolean
    multiline?: boolean | number
    requireDefault?: boolean | 'optional'
    searchPlaceholder?: string
    required?: boolean
    trigger?: { key: string, ctrl?: boolean, alt?: boolean, meta?: boolean },
    triggerDescription?: string
    allowEmpty?: boolean
    plaintext?: boolean
  }>(),
  {
    ...SHARED_PROPS_DEFAULTS,
    ...VALIDATION_PROPS_DEFAULTS,
    placeholder: undefined,
    multiline: false,
    searchPlaceholder: globalLocalize('component-library.inputs.default_search_placeholder'),
    requireDefault: false,
    open: false,
    maxLength: undefined,
    required: false,
    trigger: () => ({ key: ' ', ctrl: true }),
    triggerDescription: globalLocalize('component-library.inputs.smart_input.automation_tooltip'),
    allowEmpty: false,
    plaintext: false
  }
)

const id = useElementId(props.id)
const descriptionId = useDescriptionElementId(props.description, id.value)

const modelValue = defineModel<string | null | undefined>({ required: true })

const modelValueCasted = computed({
  get () {
    if (props.autoname) {
      return convertAutoNameToSmartString(modelValue.value)
    } else {
      return modelValue.value
    }
  },
  set (value) {
    if (props.autoname) {
      modelValue.value = convertSmartStringToAutoName(value, false)
    } else {
      modelValue.value = value
    }
  }
})

const { intermediateModel: modelValueInternal, scheduleFlush, flush } = useBuffering(modelValueCasted, props)

const { validationVisible, validationResult, isValid } = useValidation(
  modelValueInternal,
  props,
  createDefaultSmartInputValidation(props)
)

const emit = defineEmits<{
  (e: 'enter'): void
  (e: 'leave'): void
}>()

defineExpose({
  isValid,
  get isEmpty () {
    return !modelValueInternal.value
  }
})

const modelValueInternalLength = computed(() => measureSmartStringLength(modelValueInternal.value))

const contentEditableElement = useTemplateRef('content-editable')
const defaultElement = useTemplateRef('default')

const divSelection = ref<Range>()

const selectedVariable = ref<V.SmartInput.Variable>()
const selectedDefault = ref('')

const menuElement = useTemplateRef('menu')
const menuOpen = computed({
  get: () => !!divSelection.value,
  set: (value) => {
    if (value) {
      // Ignore
    } else {
      divSelection.value = undefined

      selectedDefault.value = ''
      selectedVariable.value = undefined
    }
  }
})

onClickOutsideOf(
  [
    menuElement
  ],
  () => {
    menuOpen.value = false
  },
  {
    condition: menuOpen,
    esc: true
  }
)

const updateModelValueInternal = () => {
  if (!contentEditableElement.value) return

  modelValueInternal.value = convertHtmlToSmartString(contentEditableElement.value.innerHTML)

  validationVisible.value = true
}

const select = (variable: V.SmartInput.Variable) => {
  selectedVariable.value = variable

  if (props.requireDefault) {
    defaultElement.value?.focus()
  } else {
    insert()
  }
}

const handleEnter = (event: Event) => {
  stopAndPrevent(event)

  if (props.readonly || props.disabled || !props.multiline) return

  const selection = window.getSelection()

  if (isSelectionWithinEditableArea(selection)) {
    selection.deleteFromDocument()
    selection.getRangeAt(0).insertNode(document.createTextNode("\n"))
    selection.collapseToEnd()

    updateModelValueInternal()
  }
}

const isSelectionWithinEditableArea = (selection: Selection | null): selection is Selection => {
  if (selection && (selection.focusNode === contentEditableElement.value || contentEditableElement.value?.contains(selection.focusNode))) {
    const focusNode = selection.focusNode as Element
    const anchorNode = selection.anchorNode as Element

    if ((focusNode !== anchorNode || focusNode.tagName !== 'P') && (focusNode.parentElement?.tagName !== 'P' || anchorNode.parentElement?.tagName !== 'P')) {
      // Clicked within field, ignore

      return false
    } else {
      return true
    }
  }

  return false
}

/**
 * Returns true if current event's key combination matches what would be a trigger for autocompletion
 * 
 * @param event Event
 */
const checkAutoCompleteTrigger = (event: KeyboardEvent) => {
  const { key, ctrl, alt, meta } = props.trigger

  return event.key === key && (
    typeof ctrl === 'undefined' || event.ctrlKey === !!ctrl
  ) && (
    typeof alt === 'undefined' || event.altKey === !!alt
  ) && (
    typeof meta === 'undefined' || event.metaKey === !!meta
  )
}

const checkAutoComplete = (event: KeyboardEvent) => {
  if (props.readonly || props.disabled) return
  if (props.variables.length === 0 && !props.open) return
  if (!checkAutoCompleteTrigger(event)) return

  event.stopPropagation()
  event.preventDefault()

  const selection = window.getSelection()

  if (isSelectionWithinEditableArea(selection)) {
    divSelection.value = selection.getRangeAt(0)
  }
}

const handlePaste = (event: ClipboardEvent) => {
  stopAndPrevent(event)

  if (props.readonly || props.disabled) return

  const selection = window.getSelection()

  if (isSelectionWithinEditableArea(selection)) {
    const text = event.clipboardData?.getData('text/plain') ?? ''
  
    selection.deleteFromDocument()
    selection.getRangeAt(0).insertNode(document.createTextNode(text))
    selection.collapseToEnd()

    updateModelValueInternal()
  }
}

const computedStyle = computed(() => {
  const height = typeof props.multiline === 'number' && props.multiline ? `${32 + 24 * (props.multiline - 1)}px` : undefined

  return {
    minHeight: height
  }
})

const contentEditable = computed(() => {
  if (props.readonly || props.disabled) {
    return false
  }

  if (props.plaintext) {
    return 'plaintext-only'
  }

  return true
})

const reset = () => {
  modelValueInternal.value = null
}

const insert = () => {
  if (!contentEditableElement.value) return
  if (!divSelection.value) return
  if (!selectedVariable.value?.value) return
  if (!selectedDefault.value && props.requireDefault === true) return

  const selection = window.getSelection()!

  const range = divSelection.value

  const node = document.createTextNode(' ')

  range.insertNode(node)
  range.insertNode(createSpan(selectedVariable.value.value, selectedDefault.value, variables.value))

  menuOpen.value = false
  contentEditableElement.value.focus()

  selection.removeAllRanges()

  range.setStart(node, 1)
  range.setEnd(node, 1)

  selection.addRange(range)

  updateModelValueInternal()
}

const position = useAnimationFramePosition(
  menuOpen,
  () => {
    const range = divSelection.value
    if (!range) return

    const rect = range.getClientRects()[0]

    const windowHeight = window.innerHeight

    const menuHeight = 384

    if (rect) {
      if (rect.bottom + menuHeight > windowHeight) {
        return {
          bottom: `${windowHeight - rect.y - 1}px`,
          left: `${rect.x - 6}px`
        }
      } else {
        return {
          top: `${rect.y + 15}px`,
          left: `${rect.x - 6}px`
        }
      }
    } else {
      const rect = (range.startContainer as Element).getBoundingClientRect()

      if (rect.bottom + menuHeight > windowHeight) {
        return {
          bottom: `${windowHeight - rect.top - 4}px`,
          left: `${rect.left - 6}px`
        }
      } else {
        return {
          top: `${rect.bottom - 8}px`,
          left: `${rect.left - 6}px`
        }
      }
    }
  }
)

watchEffect(() => {
  if (!contentEditableElement.value) return

  const currentText = contentEditableElement.value.innerHTML
  const compareText = convertSmartStringToHtml(modelValueInternal.value, variables.value)

  if (currentText !== compareText && currentText + '<br>' !== compareText) {
    contentEditableElement.value.innerHTML = compareText
  }
})

const variables = computed(() => {
  const instanceVariables = [
    selectedVariable.value,
    ...extractVariables(modelValueInternal.value)
  ]

  const outputVaribles = props.variables

  for (const variable of instanceVariables) {
    if (variable && !findSelectOption(variable.value, 'value', outputVaribles)) {
      // Extract variables returns { value: string, default: string }, but because we care only about the value we can just force it
      outputVaribles.unshift(variable as V.SmartInput.Variable)
    }
  }

  return outputVaribles
})

const { searchValue, searchModel, searchCollection, searchClear } = useSearch(variables, ['label', 'value'], menuOpen)

const canAdd = computed(() => {
  const value = searchValue.value

  return props.open && value && !findSelectOption(value, 'label', props.variables) && !findSelectOption(value, 'value', props.variables)
})

const add = () => {
  if (!canAdd.value) return

  const value = searchValue.value

  select({
    value,
    label: value
  })

  searchClear()
}

const onFocus = () => {
  emit('enter')
}

const onBlur = () => {
  flush()
  emit('leave')
}
</script>
